import React, { useState, useEffect } from 'react';
import {
  Button, IconButton, Typography, Box,
  FormControl, InputLabel, OutlinedInput, Tooltip,
  Autocomplete, TextField,
} from "@mui/material";
import { useSnackbar } from 'notistack';
import { ACTIONS, MESSAGES } from '../../../shared/constants';
import DataTable from '../../../shared/data-table';
import {
  getEmployees,
  getAllZones,
  getAllAirports,
  getZonesByEmployee,
  updateAllZonesByEmployee,
  deleteZones,
  bulkAssignZones,
} from './logic';
import ManagementZoneDetail from '../../gestion/management-zone/management-zoneByEmployee-Source';
import ManagementZoneBulkAssign from '../../gestion/management-zone/management-zoneBulkAssign';
import DeleteIcon from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import AddCircle from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorSnackbar from '../../error-snackbar';
import BackDrop from '../../../shared/backdropDE';
import { getUser } from '../../../shared/auth-service';
import AlertDialogQuestion from '../../alert-dialog-question';

const ManagementZone = () => {
  const [data, setData] = useState(null);
  const [showModal, setShowModal] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [action, setAction] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [optionDetails, setOptionDetails] = useState(null);
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [airports, setAirports] = useState([]);
  const [selectedAirport, setSelectedAirport] = useState(0);
  const [alertQuestionOpen, setAlertQuestionOpen] = useState(false);
  const [idDelete, setIdDelete] = useState(null);
  const companyId = user.countryCode.split('-')[0];
  const [identificationFilter, setIdentificationFilter] = useState('');
  const [dataFilter, setDataFilter] = useState([]);
  const [zoneFilter, setZoneFilter] = useState(null);
  const [airportFilter, setAirportFilter] = useState(null);
  const searchInputRef = React.useRef(null);

  useEffect(() => {
    async function fetchData() {
      if (loading) {
        try {
          const data = await getEmployees(companyId);
          const zonesData = await getAllZones(companyId);
          const airportsData = await getAllAirports(companyId);
          setSelectedAirport(user.airport.id);
          setZones(zonesData);
          setAirports(airportsData);
          setLoading(false);
          setData(data);
        } catch (error) {
          setError(error.message);
          setLoading(false);
          setData([]);
        }
      }
    }
    fetchData();
  });

  const handleFilterEmployees = (value) => {
    setIdentificationFilter(value);
  };

  useEffect(() => {
    if (!data) return;
    let result = data;

    if (identificationFilter) {
      const search = identificationFilter.toUpperCase();
      result = result.filter(item =>
        item.employee.employeeId.toString().toUpperCase().includes(search) ||
        `${item.employee.name} ${item.employee.lastName}`.toUpperCase().includes(search)
      );
    }

    if (zoneFilter) {
      result = result.filter(item =>
        item.zones.some(z => z.id === zoneFilter.id)
      );
    }

    if (airportFilter) {
      result = result.filter(item =>
        item.employee.airportId === airportFilter.id
      );
    }

    setDataFilter(result);
  }, [identificationFilter, zoneFilter, airportFilter, data]);

  const handleDeleteData = (cellValues) => {
    setAlertQuestionOpen(true);
    setIdDelete(cellValues.row.employee.employeeId);
  };

  const handleCloseAlert = () => {
    setAlertQuestionOpen(false);
    setIdDelete(null);
  };

  const clearFilters = () => {
    setIdentificationFilter('');
    setDataFilter([]);
    setZoneFilter(null);
    setAirportFilter(null);
  };

  const handleSave = async (zones) => {
    await updateAllZonesByEmployee(zones, user.employeeId);
    setShowModal(false);
    setLoading(true);
    clearFilters();
    enqueueSnackbar(MESSAGES.SAVED);
  };

  const handleBulkSave = async ({ employeeIds, zones: zoneList }) => {
    await bulkAssignZones(employeeIds, zoneList, user.employeeId);
    setShowBulkModal(false);
    setLoading(true);
    clearFilters();
    enqueueSnackbar(MESSAGES.SAVED);
  };

  const handleDelete = async () => {
    await deleteZones(idDelete, user.employeeId);
    setShowModal(false);
    setAlertQuestionOpen(false);
    setLoading(true);
    clearFilters();
    enqueueSnackbar(MESSAGES.DELETE);
  };

  const handleEdit = (e, cellValues) => {
    e.stopPropagation();
    setSelectedAirport(cellValues.row.employee.airportId);
    setEmployeeId(cellValues.row.employee.employeeId);
    fetchOptionDetails(cellValues.row.employee.employeeId);
  };

  const fetchOptionDetails = async (employeeId) => {
    try {
      const details = await getZonesByEmployee(employeeId);
      setOptionDetails(details.zones);
      setShowModal(true);
      setAction(ACTIONS.Edit);
    } catch (error) {
      setError(error.message);
    }
  };

  const columns = [
    { field: 'id', headerName: 'Código', flex: 0.2, hide: true },
    {
      field: 'employeeId', headerName: 'Identificación', flex: 0.3,
      valueGetter: (params) => `${params.row.employee.employeeId || ''}`,
    },
    {
      field: 'name', headerName: 'Nombre Completo', flex: 1,
      valueGetter: (params) => `${params.row.employee.name || ''} ${params.row.employee.lastName || ''}`,
    },
    {
      field: 'homeAirport', headerName: 'Aeropuerto', flex: 0.3,
      align: 'center', headerAlign: 'center',
      valueGetter: (params) => {
        const ap = airports.find(a => a.id === params.row.employee.airportId);
        return ap?.code || params.row.employee.airportId || '';
      },
    },
    {
      field: 'cargo', headerName: 'Cargo', flex: 0.8,
      valueGetter: (params) => `${params.row.employee.employeePosition?.name || ''}`,
    },
    {
      field: 'zoneId', headerName: 'Zonas Asignadas', flex: 0.3,
      align: 'center', headerAlign: 'center',
      valueGetter: (params) => params.row.zones.length || 0,
    },
    {
      field: "Editar", headerName: "Editar", width: 60, sortable: false,
      renderCell: (cellValues) => (
        <IconButton color="info" onClick={(e) => handleEdit(e, cellValues)}><Edit /></IconButton>
      )
    },
    {
      field: "Borrar", headerName: "Borrar", width: 60, sortable: false,
      renderCell: (cellValues) => (
        <IconButton color="error" onClick={() => handleDeleteData(cellValues)}><DeleteIcon /></IconButton>
      )
    },
  ];

  if (loading) return <BackDrop />;
  if (error) return <ErrorSnackbar message={error} />;

  return (
    <>
      {showBulkModal && (
        <ManagementZoneBulkAssign
          airports={airports}
          zones={zones}
          handleClose={() => setShowBulkModal(false)}
          handleSave={handleBulkSave}
        />
      )}

      {showModal && (
        <ManagementZoneDetail
          data={optionDetails}
          employeeId={employeeId}
          zonesSelected={zones}
          zones={zones}
          handleClose={() => setShowModal(false)}
          handleSave={handleSave}
          airportId={selectedAirport}
          airports={airports}
          action={action}
        />
      )}

      {alertQuestionOpen && (
        <AlertDialogQuestion
          handlePositive={handleDelete}
          handleNegative={handleCloseAlert}
          textButtonPositive={ACTIONS.ACCEPT}
          textButtonNegative={ACTIONS.CANCEL}
          dialogContentText={MESSAGES.CONFIRM_DELETE}
          dialogTitle=""
          open={alertQuestionOpen}
          elemento=""
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1, px: 1 }}>
        <Typography variant="h5" style={{ color: '#1a3072' }} fontWeight="bold">
          Zonas Adicionales Por <span style={{ color: '#00b0ef' }}>Empleado</span>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title="Filtrar por número de documento o nombre completo">
            <FormControl sx={{ width: 240 }} variant="outlined" size="small">
              <InputLabel htmlFor="search-zone-employee">Buscar empleado</InputLabel>
              <OutlinedInput
                id="search-zone-employee"
                inputRef={searchInputRef}
                type="text"
                value={identificationFilter}
                onChange={(e) => { setIdentificationFilter(e.target.value); handleFilterEmployees(e.target.value); }}
                endAdornment={
                  identificationFilter
                    ? <IconButton size="small" onClick={() => setIdentificationFilter('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    : <SearchIcon sx={{ color: '#aaa' }} />
                }
                label="Buscar empleado"
              />
            </FormControl>
          </Tooltip>
          <Autocomplete
            size="small"
            sx={{ width: 180 }}
            options={airports}
            getOptionLabel={(o) => o.code || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={airportFilter}
            onChange={(_, newValue) => setAirportFilter(newValue)}
            renderInput={(params) => <TextField {...params} label="Aeropuerto" variant="outlined" />}
            noOptionsText="Sin resultados"
          />
          <Autocomplete
            size="small"
            sx={{ width: 200 }}
            options={zones}
            getOptionLabel={(o) => o.description || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={zoneFilter}
            onChange={(_, newValue) => setZoneFilter(newValue)}
            renderInput={(params) => <TextField {...params} label="Zona" variant="outlined" />}
            noOptionsText="Sin resultados"
          />
          <Button endIcon={<AddCircle />} variant="contained" onClick={() => setShowBulkModal(true)}>
            {ACTIONS.NEW}
          </Button>
        </Box>
      </Box>

      <DataTable
        columns={columns}
        rows={!identificationFilter && !zoneFilter && !airportFilter ? data : dataFilter}
        getRowId={(row) => row.employee.employeeId}
        sx={{ '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' } }}
      />
    </>
  );
};

export default ManagementZone;
